import Backbone from 'backbone'
import _ from 'lodash'
import i18n from 'scoreboard/components/i18n'
import template from './label-settings.hbs'
import FormView from 'scoreboard/components/form-view'
import dropdown from 'scoreboard/components/dropdown'
import formHelper from 'scoreboard/components/form-helper'
import enums from 'scoreboard/components/enums'
import LabeledToggle from 'scoreboard/components/labeled-toggle'
import settingsHelper from './settings-helper'

/**
 * The default configuration for the label settings form. This hides fields that
 * are not available for all plot types. If a field is not in this configuration
 * that means all plot types support it
 */
const defaultConf = {
	chartedValues: true,
	chartedValuesCount: 'plural',
	stageNumbers: false,
	description: false,
	percentages: false,
	percentsRemaining: false,
	disableForMultipleSeries: true
}

/**
 * This configures the visible form fields for each type of chart. You only need
 * to add a record here if you need a control that is not visible by default
 */
const pieDonutConf = {
	percentages: true,
	abbreviateChartedValues: true, // #41299
	disableForMultipleSeries: false
}

const confByPlotType = {
	'line': { disableForMultipleSeries: false },
	'bar': { disableForMultipleSeries: false },
	'area': { disableForMultipleSeries: false },
	'waffle': { description: true, chartedValuesCount: 'singular' },
	'timeline': { stageNumbers: true, description: true, disableForMultipleSeries: false },
	'pyramid': { stageNumbers: true, disableForMultipleSeries: false },
	'funnel': { percentsRemaining: true, disableForMultipleSeries: false },
	'cycle': { stageNumbers: true, disableForMultipleSeries: false },
	'process': { stageNumbers: true, description: true, disableForMultipleSeries: false },
	'pie': pieDonutConf,
	'donut': pieDonutConf,
	'polar': { disableForMultipleSeries: false },
	'tree-map': { chartedValues: false, disableForMultipleSeries: false },
	'filled-container': { chartedValuesCount: 'singular', disableForMultipleSeries: false }
}

function getPlotTypeLabelConf(plotType) {
	const initialValues = _.clone(defaultConf)
	if (!confByPlotType[plotType]) return initialValues
	return _.extend(initialValues, confByPlotType[plotType])
}

const LabelSettings = Backbone.View.extend({
	tagName: 'form',

	className: 'label-settings',

	events: {
		'change [name=type]': 'onChangeType',
		'change [name=chartedValues]': 'onChangeChartedValuesControls',
		'change [name=abbreviateChartedValues]': 'onChangeChartedValuesControls'
	},

	/**
	 * @param {Object} [options.setting]
	 * @param {Object} [options.chartDesigner]
	 */
	initialize: function(options) {
		this.setting = _.clone(options.setting)
		this.chartDesigner = options.chartDesigner
		this.conf = getPlotTypeLabelConf(options.setting.plotType)
		this.formView = new FormView({
			el: this.el,
			submit: this.submit.bind(this),
			allowCleanSubmission: true
		})
	},

	render: function() {
		this.$el.empty().append(template(this.context()))
		this.renderPrecisionDropdown()
		formHelper.writeToDom(this.setting, this.el)

		// Initialize withe correct values
		this.onChangeChartedValuesControls()

		this.renderDescriptionToggle()
		this.disableIneligibleFields()

		this.formView.start()
		return this
	},

	renderDescriptionToggle: function() {
		if (!this.conf.description) return

		this.$('.options').toggleClass('is-not-displayed', false)
		const toggleData = enums.toToggleData('chartDescriptionType')
		let value = this.setting.descriptionType

		// Only show the scorecard/initiative options if the chart is the right
		// type
		if (!this.chartDesigner.hasScorecardItemSources()) {
			_.remove(toggleData, d => d.value === 'scorecard')
			if (value === 'scorecard') {
				value = 'none'
			}
		}
		if (!this.chartDesigner.hasInitiativeSources()) {
			_.remove(toggleData, d => d.value === 'initiative')
			if (value === 'initiative') {
				value = 'none'
			}
		}

		this.toggle = new LabeledToggle({options: toggleData})

		this.$('.toggle-container').append(this.toggle.el)
		this.toggle.render()

		this.toggle.val(value || 'none', { silent: false })
		this.toggle.on('change', this.onDescriptionTypeChanged.bind(this))
		this.onDescriptionTypeChanged(this.setting.descriptionType)
	},

	disableIneligibleFields: function() {
		const series = this.chartDesigner.features.filter(f => f.get('type') === 'serie')
		if (!this.conf.disableForMultipleSeries || series.length === 1) return

		// Only allow these label settings if there is only 1 serie
		this.$('[name="seriesLabels"]')
			.prop('checked', false) // #41712
			.prop('disabled', true)
		this.$('[name="chartedValues"]').prop('disabled', true)
		this.$('[name="abbreviateChartedValues"]').prop('disabled', true)
		this.$('.select.precision, .precision-container').toggleClass('disabled', true)
		if (this.conf.description) {
			this.toggle.val('none', { silent: false })
			this.toggle.setDisabled(true)
		}
	},

	onDescriptionTypeChanged: function(descriptionType) {
		this.setting.descriptionType = descriptionType
		this.$('textarea[name="customDescription"]')
			.toggleClass('is-not-displayed', descriptionType !== 'custom')
	},

	context: function() {
		let plotTypeLabel = settingsHelper.getPlotTypeMsgCode(this.setting.plotType)
		let settingTypeLabel = i18n.get('charts.chart.settingType.labels')
		let plotTypeSettingLabel = i18n.get('charts.chart.plotTypeSetting',
			plotTypeLabel, settingTypeLabel)
		let dialogTitle = i18n.get('common.edit2', plotTypeSettingLabel)
		let nameLabel = `charts.series.labels.${this.conf.chartedValuesCount}`
		return _.extend({ dialogTitle, nameLabel }, this.conf)
	},

	renderPrecisionDropdown: function() {
		dropdown.precision(this.$('[name=precision]'))
	},

	onChangeChartedValuesControls: function() {
		let chartedValuesChecked = this.$('[name=chartedValues]').is(':checked')
		let abbrChartedValuesChecked = this.$('[name=abbreviateChartedValues]').is(':checked')

		// Disable the precision dropdown if charted values are not checked or
		// if abbreviated values are checked
		this.$('.select.precision, .precision-container').toggleClass('disabled',
			!chartedValuesChecked || abbrChartedValuesChecked)

		// Disable the abbreviated charted values if charted values are not checked
		this.$('[name=abbreviateChartedValues]')
			.prop('disabled', !chartedValuesChecked)
	},

	submit: function() {
		_.extend(this.setting, formHelper.readFromDom(this.el))
		this.trigger('done', this.setting)
	},

	remove: function() {
		Backbone.View.prototype.remove.apply(this, arguments)
		_.invokeMap([this.formView], 'remove')
	}
})

export default LabelSettings
export { getPlotTypeLabelConf }

LabelSettings.__filename__ = 'charts/edit/label-settings.js'
